import type { SourceManifest, UserSourceGroup } from "../api";

export interface SourceGroupTab {
  id: string;
  label: string;
  sourceIds: string[];
  sources: SourceManifest[];
  builtIn?: boolean;
}

export const allSourceGroupId = "all";
const maxSourceGroupCount = 8;

export function createSourceGroupTabs(sources: SourceManifest[], groups: UserSourceGroup[]): SourceGroupTab[] {
  const normalizedGroups = normalizeSourceGroups(sources, groups);
  const sourceById = new Map(sources.map(source => [source.id, source]));

  return normalizedGroups.map(group => {
    const groupSourceIds = group.id === allSourceGroupId ? sources.map(source => source.id) : group.sourceIds;
    const groupSources = groupSourceIds.map(sourceId => sourceById.get(sourceId)).filter((source): source is SourceManifest => Boolean(source));

    return {
      id: group.id,
      label: group.name,
      sourceIds: groupSourceIds,
      sources: groupSources,
      builtIn: group.builtIn,
    };
  });
}

export function createDefaultSourceGroups(sources: SourceManifest[]): Array<Omit<UserSourceGroup, "userId" | "updatedAt">> {
  const sourceIds = sources.map(source => source.id);
  const groups: Array<Omit<UserSourceGroup, "userId" | "updatedAt">> = [
    {
      id: allSourceGroupId,
      name: "全部",
      sourceIds,
      builtIn: true,
    },
  ];

  const testSourceIds = sources.filter(source => source.id.includes("demo") || source.id.includes("test")).map(source => source.id);
  const comicSourceIds = sources.filter(source => source.kinds.includes("comic")).map(source => source.id);
  const novelSourceIds = sources.filter(source => source.kinds.includes("novel")).map(source => source.id);

  if (testSourceIds.length > 0) groups.push({ id: "test", name: "测试", sourceIds: testSourceIds, builtIn: true });
  if (comicSourceIds.length > 0) groups.push({ id: "comic", name: "漫画", sourceIds: comicSourceIds, builtIn: true });
  if (novelSourceIds.length > 0) groups.push({ id: "novel", name: "小说", sourceIds: novelSourceIds, builtIn: true });

  return groups.slice(0, maxSourceGroupCount);
}

export function toPersistedSourceGroups(
  sources: SourceManifest[],
  groups: SourceGroupTab[],
): Array<Omit<UserSourceGroup, "userId" | "updatedAt">> {
  return groups.slice(0, maxSourceGroupCount).map(group => ({
    id: group.id,
    name: group.label,
    sourceIds: group.id === allSourceGroupId ? sources.map(source => source.id) : uniqueSourceIds(group.sourceIds),
    builtIn: group.builtIn,
  }));
}

export function getSourceGroupLabels(sourceId: string, groups: SourceGroupTab[]): string[] {
  return groups
    .filter(group => group.id !== allSourceGroupId && group.sourceIds.includes(sourceId))
    .map(group => group.label);
}

export function getManagedSourceGroups(tabs: SourceGroupTab[]): SourceGroupTab[] {
  return tabs.filter(tab => tab.id !== allSourceGroupId).slice(0, maxSourceGroupCount - 1);
}

export function getMaxSourceGroupCount(): number {
  return maxSourceGroupCount;
}

function normalizeSourceGroups(sources: SourceManifest[], groups: UserSourceGroup[]): UserSourceGroup[] {
  const defaults = createDefaultSourceGroups(sources);
  const normalized = groups.length > 0 ? groups : defaults.map(group => ({ ...group, userId: "", updatedAt: "" }));
  const sourceIds = sources.map(source => source.id);
  const explicitAllGroup = normalized.find(group => group.id === allSourceGroupId) ?? normalized[0];
  const otherGroups = normalized.filter(group => group.id !== allSourceGroupId);

  return [
    {
      id: allSourceGroupId,
      userId: explicitAllGroup?.userId ?? "",
      name: explicitAllGroup?.name || "全部",
      sourceIds,
      builtIn: true,
      updatedAt: explicitAllGroup?.updatedAt ?? "",
    },
    ...otherGroups.slice(0, maxSourceGroupCount - 1).map(group => ({
      ...group,
      sourceIds: uniqueSourceIds(group.sourceIds),
    })),
  ];
}

function uniqueSourceIds(sourceIds: string[]): string[] {
  return [...new Set(sourceIds)];
}
